import { SupportedCurrency } from '../../domain/currency';
import { ExportFormat, StatType } from '../../domain/statsExport';
import { buildTransportOrdersFilters, StatsFilters } from '../../domain/statsFilters';
import { useAuthentication } from '../../modules/auth/AuthenticationProvider';
import { BACKEND_URL } from '../backend-url';

const buildQueryParams = (filters: StatsFilters, currency: SupportedCurrency, statTypes: StatType[], format: ExportFormat): URLSearchParams => {
    const params = new URLSearchParams();
    const normalizedFilters = buildTransportOrdersFilters(filters);

    if (normalizedFilters.orderMonthYearRange) {
        const { fromMonth, fromYear, toMonth, toYear } = normalizedFilters.orderMonthYearRange;
        params.append('fromMonth', fromMonth.toString());
        params.append('fromYear', fromYear.toString());
        params.append('toMonth', toMonth.toString());
        params.append('toYear', toYear.toString());
    } else {
        console.error('No orderMonthYearRange provided');
    }

    if (normalizedFilters.contractor) {
        params.append('contractorId', normalizedFilters.contractor.id);
    }
    if (normalizedFilters.creator) {
        params.append('creatorId', normalizedFilters.creator.id);
    }
    params.append('forCurrency', currency);

    // Add stat types to query params only for XLSX format
    if (format === 'xlsx') {
        statTypes.forEach(statType => {
            params.append('statTypes', statType);
        });
    }

    return params;
};

export const useStatsExportRepository = () => {
    const { getJwtToken } = useAuthentication();

    const exportStats = async (
        format: ExportFormat,
        statTypes: StatType[],
        filters: StatsFilters,
        currency: SupportedCurrency,
    ): Promise<void> => {
        const token = await getJwtToken();
        const queryParams = buildQueryParams(filters, currency, statTypes, format);

        // Build the appropriate endpoint based on format
        let endpoint = '';

        if (format === 'csv') {
            // For CSV, we need a specific endpoint for each stat type (only first one is used)
            const primaryStatType = statTypes[0]; // Only first selected type is used for CSV
            endpoint = `${BACKEND_URL}/stats-export-csv/${primaryStatType}`;
        } else if (format === 'xlsx') {
            endpoint = `${BACKEND_URL}/stats-export-xlsx`;
            // For XLSX, we send all selected stat types
        } else {
            throw new Error(`Unsupported export format: ${format}`);
        }

        const response = await fetch(
            `${endpoint}?${queryParams.toString()}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        if (!response.ok) {
            throw new Error(`${response.status}`);
        }

        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition
            ? decodeURIComponent(contentDisposition.split('filename=')[1].replace(/['"]/g, ''))
            : 'export';

        // Create a blob from the response and trigger a download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    return {
        exportStats,
    };
};
